import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Card } from "primereact/card";
import PageContainer from "../../Components/Page/PageContainer";
import { Button } from "primereact/button";
import { useLocation, useNavigate } from "react-router-dom";
import {
  StatisticPanels,
  StatisticPanelsProps,
} from "../../Components/StaticticsPanels/StatisticsPanel";
import { Skeleton } from "primereact/skeleton";
import LoadingState from "./Components/LoadingState";
import ErrorState from "./Components/ErrorState";
import AuditFilters from "./Components/FilterBars/auditFilters";
import { getCustomIDStatus } from "./Components/FilterBars/auditFilters";
import { isThirdPartyClaimPresent } from "./Components/Utils/claimsUtils";
import { useChannelManagerData } from "../../hooks/useChannelManagerData";
import { useYouTubeScorecardData } from "../../hooks/useYouTubeScorecardData";
import { RowData } from "./Interfaces/interfaces";
import {
  claimsAdsFilterOptions,
  metadataFilterOptions,
} from "./Objects/multiSelectObjects";
import {
  claimsAdsFilterOptions as claimsAdsFilterChecks,
  isMissingCustomThumbnail,
  metadataFilterOptions as metadataFilterChecks,
} from "./Objects/filterOptions";
import {
  filterYouTubeScorecardRows,
  getMyIssuesRows,
} from "../../hooks/filterYouTubeScorecardRows";
import {
  buildQueryString,
  parseBooleanParam,
  parseStringArrayParam,
} from "../../utils/urlFilters";

const MOONBUG_LEGAL_ENTITY_TYPE = "MoonbugMusicLabel";

const MoonbugMusicLabelScorecard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { channelManagerOptionsForFilter } = useChannelManagerData();

  const {
    data: metadataRows,
    loading: metadataLoading,
    error: metadataError,
    refreshData: refreshMetadata,
  } = useYouTubeScorecardData("metadata");
  const {
    data: claimsRows,
    loading: claimsLoading,
    error: claimsError,
    refreshData: refreshClaims,
  } = useYouTubeScorecardData("claimsAds");

  const user = useSelector((state: any) => state.googleAuth.user);
  const loggedInUserEmail = user?.email;

  const [filters, setFilters] = useState<any>({});
  const [myIssuesOnly, setMyIssuesOnly] = useState<boolean>(false);
  const [filteredMetadataRows, setFilteredMetadataRows] = useState<RowData[]>(
    []
  );
  const [filteredClaimsRows, setFilteredClaimsRows] = useState<RowData[]>([]);

  const moonbugMetadataRows = useMemo(
    () =>
      metadataRows.filter(
        (row) => row.LegalEntityType === MOONBUG_LEGAL_ENTITY_TYPE
      ),
    [metadataRows]
  );
  const moonbugClaimsRows = useMemo(
    () =>
      claimsRows.filter(
        (row) => row.LegalEntityType === MOONBUG_LEGAL_ENTITY_TYPE
      ),
    [claimsRows]
  );

  const myIssuesRowsMetadata = useMemo(
    () => getMyIssuesRows(moonbugMetadataRows, loggedInUserEmail),
    [moonbugMetadataRows, loggedInUserEmail]
  );
  const myIssuesRowsClaims = useMemo(
    () => getMyIssuesRows(moonbugClaimsRows, loggedInUserEmail),
    [moonbugClaimsRows, loggedInUserEmail]
  );
  const combinedMyIssuesRows = useMemo(
    () => [...myIssuesRowsMetadata, ...myIssuesRowsClaims],
    [myIssuesRowsMetadata, myIssuesRowsClaims]
  );

  const combinedFilterChecks = useMemo(
    () => ({
      ...metadataFilterChecks,
      ...claimsAdsFilterChecks,
    }),
    []
  );
  const combinedIssueSelection = useMemo(
    () => [...metadataFilterOptions, ...claimsAdsFilterOptions],
    []
  );

  const combinedMoonbugRows = useMemo(
    () => [...moonbugMetadataRows, ...moonbugClaimsRows],
    [moonbugMetadataRows, moonbugClaimsRows]
  );
  const combinedFilteredRows = useMemo(
    () => [...filteredMetadataRows, ...filteredClaimsRows],
    [filteredMetadataRows, filteredClaimsRows]
  );

  const updateUrlFromFilters = useCallback(
    (nextFilters: any) => {
      const isSelected = (value: any) =>
        value === true || value?.checked === true;
      const selectedChannelNames = Object.entries(
        nextFilters.selectedChannels || {}
      )
        .filter(([key, value]) => key.startsWith("channel___") && isSelected(value))
        .map(([key]) => key.replace("channel___", ""));
      const selectedIssueKeys = Object.entries(nextFilters.selectedFilters || {})
        .filter(([_, value]) => isSelected(value))
        .map(([key]) => key);

      const query = buildQueryString({
        videoId: nextFilters.selectedVideoID,
        cms: nextFilters.selectedCMS,
        channel: selectedChannelNames,
        channelId: nextFilters.selectedChannelID,
        channelManager: nextFilters.selectedChannelManagers,
        issue: selectedIssueKeys,
        privacyStatus: nextFilters.selectedPrivacyStatus,
        contentFormat: nextFilters.selectedContentFormat,
        legalEntity: nextFilters.selectedLegalEntity,
        legalEntityType: [MOONBUG_LEGAL_ENTITY_TYPE],
        myIssuesOnly: nextFilters.myIssuesOnly,
      });

      if (location.search === query) {
        return;
      }

      navigate({ search: query }, { replace: true });
    },
    [location.search, navigate]
  );

  const handleFilterChange = useCallback(
    (newFilters: any) => {
      const nextFilters = {
        ...newFilters,
        selectedLegalEntityType: [MOONBUG_LEGAL_ENTITY_TYPE],
      };
      setMyIssuesOnly(nextFilters.myIssuesOnly);
      setFilters(nextFilters);
      updateUrlFromFilters(nextFilters);
    },
    [updateUrlFromFilters]
  );

  const initialFilters = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      videoId: parseStringArrayParam(params, "videoId"),
      cms: parseStringArrayParam(params, "cms"),
      channel: parseStringArrayParam(params, "channel"),
      channelId: parseStringArrayParam(params, "channelId"),
      channelManager: parseStringArrayParam(params, "channelManager"),
      issue: parseStringArrayParam(params, "issue"),
      privacyStatus: parseStringArrayParam(params, "privacyStatus"),
      contentFormat: parseStringArrayParam(params, "contentFormat"),
      legalEntity: parseStringArrayParam(params, "legalEntity"),
      legalEntityType: [MOONBUG_LEGAL_ENTITY_TYPE],
      myIssuesOnly: parseBooleanParam(params, "myIssuesOnly"),
    };
  }, [location.search]);

  const selectedLegalEntity = useMemo(() => {
    const legalEntities = filters.selectedLegalEntity || [];
    return legalEntities.length > 0 ? legalEntities[0] : "";
  }, [filters.selectedLegalEntity]);

  const selectedChannelName = useMemo(() => {
    const selectedChannels = filters.selectedChannels || {};
    const channelKey = Object.keys(selectedChannels).find(
      (key) => selectedChannels[key] && key.startsWith("channel___")
    );
    return channelKey ? channelKey.replace("channel___", "") : "";
  }, [filters.selectedChannels]);

  const buildIssuesUrl = useCallback(
    (tab: "metadata" | "claims_ads") => {
      const params = new URLSearchParams();
      if (selectedLegalEntity) {
        params.set("legalEntity", selectedLegalEntity);
      }
      if (selectedChannelName) {
        params.set("channel", selectedChannelName);
      }
      const query = params.toString();
      return `/youtube_scorecard/${tab}${query ? `?${query}` : ""}`;
    },
    [selectedLegalEntity, selectedChannelName]
  );

  useEffect(() => {
    if (loggedInUserEmail) {
      setMyIssuesOnly(combinedMyIssuesRows.length > 0);
    } else {
      setMyIssuesOnly(false);
    }
  }, [combinedMyIssuesRows.length, loggedInUserEmail]);

  useEffect(() => {
    const nextMetadataRows = filterYouTubeScorecardRows({
      rows: moonbugMetadataRows,
      filters,
      filterOptions: combinedFilterChecks,
      myIssuesOnly,
      myIssuesRows: myIssuesRowsMetadata,
      loggedInUserEmail,
    });

    const nextClaimsRows = filterYouTubeScorecardRows({
      rows: moonbugClaimsRows,
      filters,
      filterOptions: combinedFilterChecks,
      myIssuesOnly,
      myIssuesRows: myIssuesRowsClaims,
      loggedInUserEmail,
    });

    setFilteredMetadataRows(nextMetadataRows);
    setFilteredClaimsRows(nextClaimsRows);
  }, [
    moonbugMetadataRows,
    moonbugClaimsRows,
    filters,
    combinedFilterChecks,
    myIssuesOnly,
    myIssuesRowsMetadata,
    myIssuesRowsClaims,
    loggedInUserEmail,
  ]);

  const [metadataStatsCards, setMetadataStatsCards] =
    useState<StatisticPanelsProps>({
      cards: [
        {
          header: "Total Issues",
          tooltip: "Total number of videos with metadata discrepancy",
          value: <Skeleton />,
          difference: <Skeleton />,
          id: "totalIssuesMetadataMoonbug",
        },
        {
          header: "Custom ID",
          tooltip: "Videos with incorrect custom ID",
          value: <Skeleton />,
          difference: <Skeleton />,
          id: "incorrectCustomIDMoonbug",
        },
        {
          header: "Metadata",
          tooltip: "Videos with missing metadata (Title, description)",
          value: <Skeleton />,
          difference: <Skeleton />,
          id: "incorrectMetadataMoonbug",
        },
        {
          header: "Category",
          tooltip: "Videos with incorrect category",
          value: <Skeleton />,
          difference: <Skeleton />,
          id: "incorrectCategoryMoonbug",
        },
        {
          header: "MFK Status",
          tooltip: "Videos with MFK status disabled",
          value: <Skeleton />,
          difference: <Skeleton />,
          id: "incorrectMFKMoonbug",
        },
        {
          header: "Custom Thumbnail",
          tooltip: "Videos without custom thumbnail",
          value: <Skeleton />,
          difference: <Skeleton />,
          id: "incorrectThumbnailMoonbug",
        },
        // {
        //   header: "Upload Language",
        //   tooltip: "Videos with incorrect Language",
        //   value: <Skeleton />,
        //   difference: <Skeleton />,
        //   id: "incorrectLanguageMoonbug",
        // },
        {
          header: "Age Restriction",
          tooltip: "Videos with incorrect Language",
          value: <Skeleton />,
          difference: <Skeleton />,
          id: "ageRestrictionMoonbug",
        },
        {
          header: "Paid Promotion",
          tooltip: "Videos with incorrect license",
          value: <Skeleton />,
          difference: <Skeleton />,
          id: "paidPromoMoonbug",
        },
        {
          header: "YouTube License",
          tooltip: "Videos with incorrect license",
          value: <Skeleton />,
          difference: <Skeleton />,
          id: "incorrectLicenseMoonbug",
        },
      ],
    });

  useEffect(() => {
    const rows = filteredMetadataRows;

    const incorrectCustomIDRows = rows.filter(
      (row) => getCustomIDStatus(row) === "Incorrect"
    );
    const incorrectThumbnailRows = rows.filter((row) =>
      isMissingCustomThumbnail(row)
    );
    const incorrectMFKRows = rows.filter((row) => row.mfk === "No");
    const incorrectCategoryRows = rows.filter(
      (row) => row.categoryStatus === "Incorrect"
    );
    const incorrectLanguageRows = rows.filter(
      (row) => row.LanguageCode === "Incorrect"
    );
    const incorrectMetadataRows = rows.filter(
      (row) =>
        row.videoTitle === "Missing" ||
        row.Description === "Missing or under 100 characters"
    );
    const incorrectLicenseRows = rows.filter(
      (row) => row.license === "Creative Common"
    );
    const ageRestrictionRows = rows.filter(
      (row) => row.ageRestriction === "Yes"
    );
    const paidPromoRow = rows.filter((row) => row.promotion === "Yes");

    setMetadataStatsCards({
      cards: [
        {
          header: "Total Issues",
          tooltip: "Total number of unique videos with any issues",
          value: rows.length.toString(),
          difference: [],
          id: "totalIssuesMetadataMoonbug",
        },
        {
          header: "Custom ID",
          tooltip: "Videos with an incorrect or missing custom ID",
          value: incorrectCustomIDRows.length.toString(),
          difference: [],
          id: "incorrectCustomIDMoonbug",
        },
        {
          header: "Metadata",
          tooltip: "Videos that are missing a title or description",
          value: incorrectMetadataRows.length.toString(),
          difference: [],
          id: "incorrectMetadataMoonbug",
        },
        {
          header: "Category",
          tooltip:
            "Videos that are assigned a YouTube video category that is not on the approved internal list",
          value: incorrectCategoryRows.length.toString(),
          difference: [],
          id: "incorrectCategoryMoonbug",
        },
        {
          header: "MFK Status",
          tooltip:
            "Videos where the MFK status is not aligned with the channels MFK designation",
          value: incorrectMFKRows.length.toString(),
          difference: [],
          id: "incorrectMFKMoonbug",
        },
        {
          header: "Custom Thumbnail",
          tooltip: "Videos that are missing a custom thumbnail",
          value: incorrectThumbnailRows.length.toString(),
          difference: [],
          id: "incorrectThumbnailMoonbug",
        },
        // {
        //   header: "Upload Language",
        //   tooltip:
        //     "Videos where the language setting on YouTube doesn't match the language of the custom ID",
        //   value: incorrectLanguageRows.length.toString(),
        //   difference: [],
        //   id: "incorrectLanguageMoonbug",
        // },
        {
          header: "Age Restriction",
          tooltip: "Videos with age-restriction enabled",
          value: ageRestrictionRows.length.toString(),
          difference: [],
          id: "ageRestrictionMoonbug",
        },
        {
          header: "Paid Promotion",
          tooltip:
            "Videos that have the paid promotion disclosure and have not been whitelisted in this tool",
          value: paidPromoRow.length.toString(),
          difference: [],
          id: "paidPromoMoonbug",
        },
        {
          header: "YouTube License",
          tooltip:
            "Videos that have the incorrect license type applied (E.g. Creative Commons)",
          value: incorrectLicenseRows.length.toString(),
          difference: [],
          id: "incorrectLicenseMoonbug",
        },
      ],
    });
  }, [filteredMetadataRows]);

  const [claimsStatsCards, setClaimsStatsCards] =
    useState<StatisticPanelsProps>({
      cards: [
        {
          header: "Total Issues",
          tooltip: "Count of unique videos with any issues",
          value: <Skeleton />,
          difference: <Skeleton />,
          id: "totalIssuesClaimsAdsMoonbug",
        },
        // {
        //   header: "Asset Ownership",
        //   tooltip: "Moonbug owned YouTube assets missing ownership",
        //   value: <Skeleton />,
        //   difference: <Skeleton />,
        //   id: "unownedAssetsClaimsAdsMoonbug",
        // },
        {
          header: "Third Party Claims",
          tooltip: "Videos which have been claimed by a third party",
          value: <Skeleton />,
          difference: <Skeleton />,
          id: "thirdPartyClaimsClaimsAdsMoonbug",
        },
        {
          header: "Sync Composition Claims",
          tooltip:
            "Videos which have been claimed by a third party for sync composition rights",
          value: <Skeleton />,
          difference: <Skeleton />,
          id: "syncThirdPartyClaimsClaimsAdsMoonbug",
        },
        {
          header: "Unclaimed Videos",
          tooltip: "Videos which have not been claimed and monetized by us",
          value: <Skeleton />,
          difference: <Skeleton />,
          id: "unclaimedVideosClaimsAdsMoonbug",
        },
        {
          header: "Missing Pre-Rolls",
          tooltip: "Videos missing Pre-Rolls",
          value: <Skeleton />,
          difference: <Skeleton />,
          id: "missingPreRollsClaimsAdsMoonbug",
        },
        {
          header: "Missing Mid-Rolls",
          tooltip: "Videos missing Mid-Rolls (unless on the exceptions list)",
          value: <Skeleton />,
          difference: <Skeleton />,
          id: "missingMidRollsClaimsAdsMoonbug",
        },
        {
          header: "Missing Post-Rolls",
          tooltip: "Videos missing Post-Rolls",
          value: <Skeleton />,
          difference: <Skeleton />,
          id: "missingPostRollsClaimsAdsMoonbug",
        },
        {
          header: "Mid-Roll Frequency",
          tooltip: "Average mid-roll frequency <2mins or >6mins",
          value: <Skeleton />,
          difference: <Skeleton />,
          id: "midRollFrequencyClaimsAdsMoonbug",
        },
        {
          header: "Mid-Roll Interval",
          tooltip: "Largest outlier with mid-rolls interval <2mins or >6mins",
          value: <Skeleton />,
          difference: <Skeleton />,
          id: "midRollIntervalClaimsAdsMoonbug",
        },
        {
          header: "Auto Generated Ads",
          tooltip: "Videos with auto-generated ads not enabled",
          value: <Skeleton />,
          difference: <Skeleton />,
          id: "autoGeneratedAdsMoonbug",
        },
      ],
    });

  useEffect(() => {
    const rows = filteredClaimsRows;

    // const unownedAssets = rows.filter((row) => row.ownership === "No");
    const thirdParty = rows.filter((row) => isThirdPartyClaimPresent(row));
    const syncThirdParty = rows.filter((row) => row.syncThirdPartyClaim === "Yes");
    const unclaimedVideos = rows.filter((row) => row.claimStatus === "No");
    const preRollConflicts = rows.filter((row) =>
      claimsAdsFilterChecks.missingPreRoll(row)
    );
    const midRollConflicts = rows.filter((row) =>
      claimsAdsFilterChecks.missingMidRolls(row)
    );
    const postRollConflicts = rows.filter((row) =>
      claimsAdsFilterChecks.missingPostRolls(row)
    );
    const midRollFrequency = rows.filter((row) =>
      claimsAdsFilterChecks.midRollFrequency(row)
    );
    const midRollInterval = rows.filter((row) =>
      claimsAdsFilterChecks.midRollInterval(row)
    );
    const autoGeneratedAds = rows.filter((row) =>
      claimsAdsFilterChecks.autoGeneratedAds(row)
    );

    setClaimsStatsCards({
      cards: [
        {
          header: "Total Issues",
          tooltip: "Count of unique videos with any issues",
          value: rows.length.toString(),
          difference: [],
          id: "totalIssuesClaimsAdsMoonbug",
        },
        // {
        //   header: "Asset Ownership",
        //   tooltip: "Moonbug-owned YouTube assets where ownership is not 'Worldwide'",
        //   value: unownedAssets.length.toString(),
        //   difference: [],
        //   id: "unownedAssetsClaimsAdsMoonbug",
        // },
        {
          header: "Third Party Claims",
          tooltip: "Videos that have an active content ID claim from another entity",
          value: thirdParty.length.toString(),
          difference: [],
          id: "thirdPartyClaimsClaimsAdsMoonbug",
        },
        {
          header: "Sync Composition Claims",
          tooltip:
            "Videos that have an active sync composition claim from another entity",
          value: syncThirdParty.length.toString(),
          difference: [],
          id: "syncThirdPartyClaimsClaimsAdsMoonbug",
        },
        {
          header: "Unclaimed Videos",
          tooltip: "Videos that haven't been claimed and monetized by Moonbug",
          value: unclaimedVideos.length.toString(),
          difference: [],
          id: "unclaimedVideosClaimsAdsMoonbug",
        },
        {
          header: "Missing Pre-Rolls",
          tooltip: "Videos missing pre-roll ads",
          value: preRollConflicts.length.toString(),
          difference: [],
          id: "missingPreRollsClaimsAdsMoonbug",
        },
        {
          header: "Missing Mid-Rolls",
          tooltip: "Videos missing mid-roll ads",
          value: midRollConflicts.length.toString(),
          difference: [],
          id: "missingMidRollsClaimsAdsMoonbug",
        },
        {
          header: "Missing Post-Rolls",
          tooltip: "Videos missing post-roll ads",
          value: postRollConflicts.length.toString(),
          difference: [],
          id: "missingPostRollsClaimsAdsMoonbug",
        },
        {
          header: "Mid-Roll Frequency",
          tooltip:
            "Videos where the average time between mid-roll ads is either too short or too long",
          value: midRollFrequency.length.toString(),
          difference: [],
          id: "midRollFrequencyClaimsAdsMoonbug",
        },
        {
          header: "Mid-Roll Interval",
          tooltip:
            "Videos where the time between any inidividual mid-roll is either too long or too short",
          value: midRollInterval.length.toString(),
          difference: [],
          id: "midRollIntervalClaimsAdsMoonbug",
        },
        {
          header: "Auto Generated Ads",
          tooltip: "Videos with auto-generated ads not enabled",
          value: autoGeneratedAds.length.toString(),
          difference: [],
          id: "autoGeneratedAdsMoonbug",
        },
      ],
    });
  }, [filteredClaimsRows]);

  const moonbugExportRows = useCallback((row: RowData) => {
    return {
      "Video ID": row.videoId ?? "",
      "Channel Name": row.Channel_Name ?? "",
      "Channel ID": row.YoutubeChannelId ?? "",
      CMS: row.CMS ?? "",
      "Legal Entity": row.LegalEntity ?? "",
      "Legal Entity Type": row.LegalEntityType ?? "",
      "Custom ID": row.customid ?? "",
      "Video Title": row.videoTitle ?? "",
    };
  }, []);

  const isLoading = metadataLoading || claimsLoading;
  const error = metadataError || claimsError;

  if (isLoading) {
    return <LoadingState message="Loading Moonbug Music Label scorecard..." />;
  }

  if (error) {
    return (
      <ErrorState
        error={error}
        onRetry={() => {
          refreshMetadata();
          refreshClaims();
        }}
        title="Error Loading Moonbug Music Label Scorecard"
      />
    );
  }

  return (
    <PageContainer
      HomeMenuBarprops={{
        title: "Moonbug Music Label Scorecard",
      }}
    >
      <div
        className="main-content"
        style={{
          width: "96%",
          margin: "0 auto",
        }}
      >
        <Card
          unstyled
          style={{
            marginTop: "2rem",
            boxShadow: "none",
          }}
        >
          <div style={{ width: "100%", marginTop: "-30px", marginBottom: "-30px" }}>
            <div>
              <AuditFilters
                rows={combinedMoonbugRows}
                onFilterChange={handleFilterChange}
                issueSelction={combinedIssueSelection}
                exportColumns={moonbugExportRows}
                filteredRows={combinedFilteredRows}
                disabled={combinedMyIssuesRows.length <= 0}
                exportName={"moonbug_music_label_scorecard"}
                myIssuesOnly={myIssuesOnly}
                channelManagerOptions={channelManagerOptionsForFilter}
                initialVideoId={initialFilters.videoId}
                initialCMS={initialFilters.cms}
                initialChannelNames={initialFilters.channel}
                initialChannelId={initialFilters.channelId}
                initialChannelManagers={initialFilters.channelManager}
                initialIssueKeys={initialFilters.issue}
                initialPrivacyStatus={initialFilters.privacyStatus}
                initialContentFormat={initialFilters.contentFormat}
                initialLegalEntity={initialFilters.legalEntity}
                initialLegalEntityType={initialFilters.legalEntityType}
                initialMyIssuesOnly={initialFilters.myIssuesOnly}
                forcedLegalEntityType={[MOONBUG_LEGAL_ENTITY_TYPE]}
                footerAction={
                  <div
                    style={{
                      display: "flex",
                      gap: "0.75rem",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Button
                      label="View Metadata Issues"
                      icon="pi pi-external-link"
                      onClick={() => navigate(buildIssuesUrl("metadata"))}
                      severity="secondary"
                    />
                    <Button
                      label="View Claims & Ads Issues"
                      icon="pi pi-external-link"
                      onClick={() => navigate(buildIssuesUrl("claims_ads"))}
                      severity="secondary"
                    />
                  </div>
                }
              />
              <h3 style={{ marginTop: "1.5rem" }}>Metadata & Properties</h3>
              <StatisticPanels cards={metadataStatsCards.cards} />
              <h3 style={{ marginTop: "2rem" }}>Claims & Ads</h3>
              <StatisticPanels cards={claimsStatsCards.cards} />
            </div>
          </div>
        </Card>
      </div>
    </PageContainer>
  );
};

export default MoonbugMusicLabelScorecard;
